import { APP_LOCALES, buildGraph, KIN_PACK_IDS, KIN_PACKS, parseBackup, relate, sampleBackup, type AppLocale, type KinPack, type KinPackId } from '@lineage/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useToast } from '../../components/Toast';
import { useT } from '../../i18n';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { withCode } from '../../lib/errorCode';
import { deviceLocale, useKinPack, useLocale, usePrefs } from '../../lib/prefs';
import { keys } from '../../lib/queries';
import { UPCOMING_APP_LANGUAGES, UPCOMING_WORD_SETS, type UpcomingId } from './upcoming';

/** Each app language named in itself, so people can find theirs. */
const LOCALE_NAMES: Record<AppLocale, string> = { en: 'English', es: 'Español' };

/** A few words from each set, shown in that set's own spelling so people can recognise it. */
const EXAMPLES: Record<KinPackId, string> = {
  en: 'Uncle · Aunt · Cousin',
  es: 'Tío · Tía · Primo',
  gu: 'કાકા · માસી · ફોઈ',
  'gu-Latn': 'Kaka · Masi · Foi',
  hi: 'चाचा · मौसी · बुआ',
  'hi-Latn': 'Chacha · Mausi · Bua',
  pa: 'ਚਾਚਾ · ਮਾਸੀ · ਭੂਆ',
  'pa-Latn': 'Chacha · Masi · Bhua',
  ar: 'عم · خالة · ابن العم',
  'ar-Latn': 'Amm · Khala · Ibn al-Amm',
};

interface WordsOption {
  value: KinPackId | null;
  /** React key when `value` can't tell options apart (the Coming soon entries have none). */
  key?: string;
  title: string;
  detail: string;
  examples?: string;
  lang?: string;
  /** Arabic reads right to left inside the list. */
  dir?: 'ltr' | 'rtl';
  beta?: boolean;
  /** Planned, not built: shown but not selectable. */
  soon?: boolean;
}

/** An option's text: its name with a beta badge, what it is written in, and (in the open list) example words. */
function OptionText({ o, compact }: { o: WordsOption; compact?: boolean }) {
  const t = useT().settings;
  return (
    <span className="choice-body">
      <span className="choice-title">
        <span lang={o.lang} dir={o.dir}>{o.title}</span>
        {o.beta && <span className="pill">{t.beta}</span>}
        {o.soon && <span className="pill pill-soon">{t.comingSoon}</span>}
      </span>
      <small className="muted">{o.detail}</small>
      {!compact && o.examples && <small className="choice-examples" lang={o.lang} dir={o.dir}>{o.examples}</small>}
    </span>
  );
}

/**
 * The relationship-words dropdown. A native <select> can't show a second line or a badge, so this is a
 * listbox: the button shows the current choice, the list shows every option with what it is and example words.
 * Focus stays on the button; arrows move the highlight, Enter or Space picks, Escape closes.
 */
function WordsPicker({ options, value, onChange, label }: { options: WordsOption[]; value: KinPackId | null; onChange: (v: KinPackId | null) => void; label: string }) {
  const id = useId();
  const root = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const selected = Math.max(0, options.findIndex((o) => o.value === value));
  const [active, setActive] = useState(selected);

  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', away);
    return () => document.removeEventListener('pointerdown', away);
  }, [open]);

  const show = () => {
    setActive(selected);
    setOpen(true);
  };
  const pick = (i: number) => {
    setOpen(false);
    const v = options[i]!.value;
    if (v !== value) onChange(v);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    const last = options.filter((o) => !o.soon).length - 1; // Coming soon entries sit at the end
    if (e.key === 'Escape' && open) { e.preventDefault(); setOpen(false); }
    else if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      if (!open) return show();
      setActive((a) => (e.key === 'Home' ? 0 : e.key === 'End' ? last : e.key === 'ArrowDown' ? Math.min(a + 1, last) : Math.max(a - 1, 0)));
    } else if ((e.key === 'Enter' || e.key === ' ') && open) {
      e.preventDefault();
      pick(active);
    }
  };

  return (
    <div className="picker" ref={root}>
      <button
        type="button"
        className="input picker-trigger"
        role="combobox"
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={`${id}-list`}
        aria-activedescendant={open ? `${id}-${active}` : undefined}
        onClick={() => (open ? setOpen(false) : show())}
        onKeyDown={onKeyDown}
        onBlur={() => setOpen(false)}
      >
        <OptionText o={options[selected]!} compact />
        <span className="picker-chevron" aria-hidden="true" />
      </button>
      {open && (
        <div className="picker-list" role="listbox" id={`${id}-list`} aria-label={label}>
          {options.map((o, i) => (
            <div
              key={o.key ?? o.value ?? 'auto'}
              id={`${id}-${i}`}
              role="option"
              aria-selected={i === selected}
              aria-disabled={o.soon || undefined}
              className="picker-option"
              data-active={i === active && !o.soon}
              onPointerEnter={() => !o.soon && setActive(i)}
              onPointerDown={(e) => e.preventDefault() /* keep focus on the button so its blur doesn't close the list first */}
              onClick={() => !o.soon && pick(i)}
            >
              <OptionText o={o} />
              {i === selected && <span className="picker-check" aria-hidden="true">✓</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** People in the sample family whose labels differ most between languages, as Leo sees them. */
const PREVIEW = ['walter', 'ronald', 'beth', 'sophie', 'arjun', 'mark'];

function Preview({ pack }: { pack: KinPack }) {
  const t = useT().settings;
  const rows = useMemo(() => {
    const data = parseBackup(sampleBackup());
    const g = buildGraph(data.people, data.parentLinks, data.partnerships);
    const me = data.idMap.leo!;
    return PREVIEW.map((key) => {
      const id = data.idMap[key]!;
      return { key, name: g.people.get(id)!.firstName ?? key, kin: relate(g, me, id) };
    });
  }, []);
  return (
    <div className="stack" style={{ gap: 8 }}>
      <p className="muted" style={{ margin: 0 }}>{t.preview}</p>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
        {rows.map((r) => (
          <li key={r.key} style={{ display: 'grid', gridTemplateColumns: '5.5em 1fr', columnGap: 12 }}>
            <strong>{r.name}</strong>
            <span lang={pack.lang} dir={pack.dir}>
              {pack.label(r.kin)}
              <small className="muted" style={{ display: 'block' }}>{pack.explain(r.kin)}</small>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Saves a preference on this device straight away and on the account when signed in; undoes it if that fails. */
function useSave<T extends string>(field: 'kinPack' | 'locale', current: T | null, setLocal: (v: T | null) => void) {
  const t = useT().settings;
  const { user } = useAuth();
  const qc = useQueryClient();
  const toast = useToast((s) => s.show);
  const save = useMutation({
    mutationFn: (v: T | null) => api('PATCH', '/me', { [field]: v }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.me }),
  });
  return (v: T | null) => {
    const before = current;
    setLocal(v);
    if (!user) return;
    save.mutate(v, {
      onError: (e) => {
        setLocal(before);
        toast(e instanceof ApiError ? e.message : withCode(t.couldntSaveChoice, e));
      },
    });
  };
}

/** What each preference is set to right now, for the value shown on the Settings list. */
export function useLanguageSummary() {
  const t = useT().settings;
  const prefs = usePrefs();
  const locale = useLocale();
  return {
    language: prefs.locale === null ? t.automatic(LOCALE_NAMES[deviceLocale()]) : LOCALE_NAMES[prefs.locale],
    words: prefs.kinPack === null ? t.automatic(t.wordSets[locale].title) : t.wordSets[prefs.kinPack].title,
  };
}

/** Settings → App language (PRD 11.1): Automatic follows the phone or browser, or pick one. */
export function AppLanguagePanel() {
  const t = useT().settings;
  const locale = useLocale();
  const prefs = usePrefs();
  const chooseLocale = useSave<AppLocale>('locale', prefs.locale, prefs.setLocale);
  return (
    <div className="settings-choices" role="group" aria-label={t.appLanguage}>
      <button type="button" className="settings-choice" aria-pressed={prefs.locale === null} onClick={() => chooseLocale(null)}>
        <strong>{t.automaticTitle}</strong>
        <small>{t.automaticFollows(LOCALE_NAMES[deviceLocale()])}</small>
      </button>
      {APP_LOCALES.map((l) => (
        <button key={l} type="button" className="settings-choice" lang={l} aria-pressed={prefs.locale === l} onClick={() => chooseLocale(l)}>
          <strong>{LOCALE_NAMES[l]}</strong>
        </button>
      ))}
      {UPCOMING_APP_LANGUAGES.map((u) => (
        <button key={u.id} type="button" className="settings-choice" lang={u.lang} disabled>
          <strong dir={u.dir}>{u.native}</strong>
          <small lang={locale}>{t.upcoming[u.id as UpcomingId]}</small>
          <span className="pill pill-soon" lang={locale} dir="auto">{t.comingSoon}</span>
        </button>
      ))}
    </div>
  );
}

/** Settings → Relationship words (PRD 11.1): the dropdown of word sets and a live preview. */
export function WordsPanel() {
  const t = useT().settings;
  const prefs = usePrefs();
  const locale = useLocale();
  const pack = useKinPack();
  const chooseWords = useSave<KinPackId>('kinPack', prefs.kinPack, prefs.setKinPack);
  return (
    <div className="stack">
      <div className="field">
        <span>{t.wordsHint}</span>
        <WordsPicker
          label={t.words}
          value={prefs.kinPack}
          onChange={chooseWords}
          options={[
            { value: null, title: t.automatic(t.wordSets[locale].title), detail: t.wordsAutomaticHint },
            ...KIN_PACK_IDS.map((id) => ({
              value: id,
              title: t.wordSets[id].title,
              detail: t.wordSets[id].how,
              examples: EXAMPLES[id],
              lang: KIN_PACKS[id].lang,
              dir: KIN_PACKS[id].dir,
              beta: KIN_PACKS[id].status === 'beta',
            })),
            ...UPCOMING_WORD_SETS.map((u) => ({
              value: null,
              key: `soon-${u.id}`,
              title: u.native,
              detail: t.upcoming[u.id as UpcomingId],
              lang: u.lang,
              dir: u.dir,
              soon: true,
            })),
          ]}
        />
      </div>
      <Preview pack={pack} />
    </div>
  );
}
